import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { appendCanvasRulesToUserMessage } from '../constants/canvasRules';
import { useAuth } from '../contexts';
import { executingAgentService } from '../services';
import {
  AnalysisArtifacts,
  AssistantPhase,
  ConversationImageAttachment,
  DEFAULT_SEARCH_CONTEXT_TYPE,
  ImageAnalysisStatus,
  Message,
  SearchContextType,
} from '../types/conversation';
import { useTokens } from './useTokens';

interface StopContext {
  tabId: string;
  messageId: string;
  updateTabMessages: (tabId: string, messages: Message[]) => void;
  messages: Message[];
  userMessage: string;
  restoreDraftMessage?: (tabId: string, message: string) => void;
  getCurrentMessages?: (tabId: string) => Message[];
}

export const normalizeAnalysisArtifacts = (
  payload: unknown
): AnalysisArtifacts | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  const source = payload as Record<string, unknown>;
  const xlsx_base64 =
    typeof source.xlsx_base64 === 'string' ? source.xlsx_base64.trim() : '';
  const csv_base64 =
    typeof source.csv_base64 === 'string' ? source.csv_base64.trim() : '';
  const pdf_base64 =
    typeof source.pdf_base64 === 'string' ? source.pdf_base64.trim() : '';
  const docx_base64 =
    typeof source.docx_base64 === 'string' ? source.docx_base64.trim() : '';
  const pptx_base64 =
    typeof source.pptx_base64 === 'string' ? source.pptx_base64.trim() : '';
  const xlsx_artifact_id =
    typeof source.xlsx_artifact_id === 'string'
      ? source.xlsx_artifact_id.trim()
      : '';
  const csv_artifact_id =
    typeof source.csv_artifact_id === 'string'
      ? source.csv_artifact_id.trim()
      : '';
  const pdf_artifact_id =
    typeof source.pdf_artifact_id === 'string'
      ? source.pdf_artifact_id.trim()
      : '';
  const docx_artifact_id =
    typeof source.docx_artifact_id === 'string'
      ? source.docx_artifact_id.trim()
      : '';
  const pptx_artifact_id =
    typeof source.pptx_artifact_id === 'string'
      ? source.pptx_artifact_id.trim()
      : '';
  const xlsx_download_url =
    typeof source.xlsx_download_url === 'string'
      ? source.xlsx_download_url.trim()
      : '';
  const csv_download_url =
    typeof source.csv_download_url === 'string'
      ? source.csv_download_url.trim()
      : '';
  const pdf_download_url =
    typeof source.pdf_download_url === 'string'
      ? source.pdf_download_url.trim()
      : '';
  const docx_download_url =
    typeof source.docx_download_url === 'string'
      ? source.docx_download_url.trim()
      : '';
  const pptx_download_url =
    typeof source.pptx_download_url === 'string'
      ? source.pptx_download_url.trim()
      : '';
  const resolvedXlsxDownloadUrl =
    xlsx_download_url ||
    (xlsx_artifact_id
      ? `/v1/execute-ai/analysis-artifacts/${encodeURIComponent(
          xlsx_artifact_id
        )}/download`
      : '');
  const resolvedCsvDownloadUrl =
    csv_download_url ||
    (csv_artifact_id
      ? `/v1/execute-ai/analysis-artifacts/${encodeURIComponent(
          csv_artifact_id
        )}/download`
      : '');
  const resolvedPdfDownloadUrl =
    pdf_download_url ||
    (pdf_artifact_id
      ? `/v1/execute-ai/analysis-artifacts/${encodeURIComponent(
          pdf_artifact_id
        )}/download`
      : '');
  const resolvedDocxDownloadUrl =
    docx_download_url ||
    (docx_artifact_id
      ? `/v1/execute-ai/analysis-artifacts/${encodeURIComponent(
          docx_artifact_id
        )}/download`
      : '');
  const resolvedPptxDownloadUrl =
    pptx_download_url ||
    (pptx_artifact_id
      ? `/v1/execute-ai/analysis-artifacts/${encodeURIComponent(
          pptx_artifact_id
        )}/download`
      : '');
  const shouldKeepXlsxBase64 = !xlsx_artifact_id && !resolvedXlsxDownloadUrl;
  const shouldKeepCsvBase64 = !csv_artifact_id && !resolvedCsvDownloadUrl;
  const shouldKeepPdfBase64 = !pdf_artifact_id && !resolvedPdfDownloadUrl;
  const shouldKeepDocxBase64 = !docx_artifact_id && !resolvedDocxDownloadUrl;
  const shouldKeepPptxBase64 = !pptx_artifact_id && !resolvedPptxDownloadUrl;
  const xlsx_file_name =
    typeof source.xlsx_file_name === 'string' ? source.xlsx_file_name.trim() : '';
  const csv_file_name =
    typeof source.csv_file_name === 'string' ? source.csv_file_name.trim() : '';
  const pdf_file_name =
    typeof source.pdf_file_name === 'string' ? source.pdf_file_name.trim() : '';
  const docx_file_name =
    typeof source.docx_file_name === 'string' ? source.docx_file_name.trim() : '';
  const pptx_file_name =
    typeof source.pptx_file_name === 'string' ? source.pptx_file_name.trim() : '';
  const xlsx_mime_type =
    typeof source.xlsx_mime_type === 'string' ? source.xlsx_mime_type.trim() : '';
  const csv_mime_type =
    typeof source.csv_mime_type === 'string' ? source.csv_mime_type.trim() : '';
  const pdf_mime_type =
    typeof source.pdf_mime_type === 'string' ? source.pdf_mime_type.trim() : '';
  const docx_mime_type =
    typeof source.docx_mime_type === 'string' ? source.docx_mime_type.trim() : '';
  const pptx_mime_type =
    typeof source.pptx_mime_type === 'string' ? source.pptx_mime_type.trim() : '';

  if (
    !xlsx_base64 &&
    !csv_base64 &&
    !pdf_base64 &&
    !docx_base64 &&
    !pptx_base64 &&
    !xlsx_artifact_id &&
    !csv_artifact_id &&
    !pdf_artifact_id &&
    !docx_artifact_id &&
    !pptx_artifact_id &&
    !resolvedXlsxDownloadUrl &&
    !resolvedCsvDownloadUrl &&
    !resolvedPdfDownloadUrl &&
    !resolvedDocxDownloadUrl &&
    !resolvedPptxDownloadUrl
  ) {
    return null;
  }

  const normalized: AnalysisArtifacts = {
    ...(xlsx_base64 && shouldKeepXlsxBase64 ? { xlsx_base64 } : {}),
    ...(csv_base64 && shouldKeepCsvBase64 ? { csv_base64 } : {}),
    ...(pdf_base64 && shouldKeepPdfBase64 ? { pdf_base64 } : {}),
    ...(docx_base64 && shouldKeepDocxBase64 ? { docx_base64 } : {}),
    ...(pptx_base64 && shouldKeepPptxBase64 ? { pptx_base64 } : {}),
    ...(xlsx_artifact_id ? { xlsx_artifact_id } : {}),
    ...(csv_artifact_id ? { csv_artifact_id } : {}),
    ...(pdf_artifact_id ? { pdf_artifact_id } : {}),
    ...(docx_artifact_id ? { docx_artifact_id } : {}),
    ...(pptx_artifact_id ? { pptx_artifact_id } : {}),
    ...(resolvedXlsxDownloadUrl ? { xlsx_download_url: resolvedXlsxDownloadUrl } : {}),
    ...(resolvedCsvDownloadUrl ? { csv_download_url: resolvedCsvDownloadUrl } : {}),
    ...(resolvedPdfDownloadUrl ? { pdf_download_url: resolvedPdfDownloadUrl } : {}),
    ...(resolvedDocxDownloadUrl ? { docx_download_url: resolvedDocxDownloadUrl } : {}),
    ...(resolvedPptxDownloadUrl ? { pptx_download_url: resolvedPptxDownloadUrl } : {}),
    ...(xlsx_file_name ? { xlsx_file_name } : {}),
    ...(csv_file_name ? { csv_file_name } : {}),
    ...(pdf_file_name ? { pdf_file_name } : {}),
    ...(docx_file_name ? { docx_file_name } : {}),
    ...(pptx_file_name ? { pptx_file_name } : {}),
    ...(xlsx_mime_type ? { xlsx_mime_type } : {}),
    ...(csv_mime_type ? { csv_mime_type } : {}),
    ...(pdf_mime_type ? { pdf_mime_type } : {}),
    ...(docx_mime_type ? { docx_mime_type } : {}),
    ...(pptx_mime_type ? { pptx_mime_type } : {}),
  };

  if (source.xlsx_is_complete === true) {
    normalized.xlsx_is_complete = true;
  } else if (source.xlsx_is_complete === false) {
    normalized.xlsx_is_complete = false;
  } else if (xlsx_base64 || xlsx_artifact_id || resolvedXlsxDownloadUrl) {
    normalized.xlsx_is_complete = true;
  }
  if (source.truncated === true) {
    normalized.truncated = true;
  }
  if (source.csv_is_complete === true) {
    normalized.csv_is_complete = true;
  } else if (source.csv_is_complete === false) {
    normalized.csv_is_complete = false;
  } else if (csv_base64 || csv_artifact_id || resolvedCsvDownloadUrl) {
    normalized.csv_is_complete = true;
  }
  if (source.pdf_is_complete === true) {
    normalized.pdf_is_complete = true;
  } else if (source.pdf_is_complete === false) {
    normalized.pdf_is_complete = false;
  } else if (pdf_base64 || pdf_artifact_id || resolvedPdfDownloadUrl) {
    normalized.pdf_is_complete = true;
  }
  if (source.docx_is_complete === true) {
    normalized.docx_is_complete = true;
  } else if (source.docx_is_complete === false) {
    normalized.docx_is_complete = false;
  } else if (docx_base64 || docx_artifact_id || resolvedDocxDownloadUrl) {
    normalized.docx_is_complete = true;
  }
  if (source.pptx_is_complete === true) {
    normalized.pptx_is_complete = true;
  } else if (source.pptx_is_complete === false) {
    normalized.pptx_is_complete = false;
  } else if (pptx_base64 || pptx_artifact_id || resolvedPptxDownloadUrl) {
    normalized.pptx_is_complete = true;
  }
  if (source.is_complete === true) {
    normalized.is_complete = true;
  } else if (source.is_complete === false) {
    normalized.is_complete = false;
  } else if (
    normalized.xlsx_is_complete ||
    normalized.csv_is_complete ||
    normalized.pdf_is_complete ||
    normalized.docx_is_complete ||
    normalized.pptx_is_complete
  ) {
    normalized.is_complete = true;
  }

  const totalRows = Number(source.total_rows);
  if (Number.isFinite(totalRows) && totalRows >= 0) {
    normalized.total_rows = totalRows;
  }

  const previewRows = Number(source.preview_rows);
  if (Number.isFinite(previewRows) && previewRows >= 0) {
    normalized.preview_rows = previewRows;
  }
  const artifactTotalRows = Number(source.artifact_total_rows);
  if (Number.isFinite(artifactTotalRows) && artifactTotalRows >= 0) {
    normalized.artifact_total_rows = artifactTotalRows;
  }

  return normalized;
};

const normalizeImageAnalysis = (payload: unknown): ImageAnalysisStatus | null => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  const source = payload as Record<string, unknown>;
  const normalized: ImageAnalysisStatus = {};

  if (typeof source.status === 'string' && source.status.trim()) {
    normalized.status = source.status.trim();
  }
  if (typeof source.notice === 'string' && source.notice.trim()) {
    normalized.notice = source.notice.trim();
  }

  const totalImages = Number(source.total_images);
  if (Number.isFinite(totalImages) && totalImages >= 0) {
    normalized.total_images = totalImages;
  }

  const processedImages = Number(source.processed_images);
  if (Number.isFinite(processedImages) && processedImages >= 0) {
    normalized.processed_images = processedImages;
  }

  const skippedBySize = Number(source.skipped_by_size);
  if (Number.isFinite(skippedBySize) && skippedBySize >= 0) {
    normalized.skipped_by_size = skippedBySize;
  }

  if (typeof source.model === 'string' && source.model.trim()) {
    normalized.model = source.model.trim();
  }
  if (typeof source.fallback_reason === 'string' && source.fallback_reason.trim()) {
    normalized.fallback_reason = source.fallback_reason.trim();
  }
  if (typeof source.error_type === 'string' && source.error_type.trim()) {
    normalized.error_type = source.error_type.trim();
  }
  const visionContextChars = Number(source.vision_context_chars);
  if (Number.isFinite(visionContextChars) && visionContextChars >= 0) {
    normalized.vision_context_chars = visionContextChars;
  }

  return Object.keys(normalized).length > 0 ? normalized : null;
};

export const useMessageSending = () => {
  const { t } = useTranslation();
  const [streamingByTab, setStreamingByTab] = useState<Record<string, boolean>>(
    {}
  );
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  const stopContextRef = useRef<Record<string, StopContext>>({});
  const cancelledTabsRef = useRef<Set<string>>(new Set());
  const { checkTokensBeforeAction } = useTokens();
  const { refreshUser } = useAuth();

  const setTabStreaming = useCallback((tabId: string, value: boolean) => {
    setStreamingByTab(prev => {
      if (value) {
        return { ...prev, [tabId]: true };
      }

      if (!prev[tabId]) {
        return prev;
      }

      const next = { ...prev };
      delete next[tabId];
      return next;
    });
  }, []);

  const isTabStreaming = useCallback(
    (tabId: string) => Boolean(streamingByTab[tabId]),
    [streamingByTab]
  );

  const sendMessage = useCallback(
    async (
      messageText: string,
      attachments: ConversationImageAttachment[],
      activeTab: any,
      useCanvas: boolean,
      updateTabMessages: (tabId: string, messages: Message[]) => void,
      updateTabSessionId: (tabId: string, sessionId: string) => void,
      restoreDraftMessage?: (tabId: string, message: string) => void,
      getCurrentMessages?: (tabId: string) => Message[]
    ) => {
      if (
        (!messageText.trim() && attachments.length === 0) ||
        !activeTab ||
        !activeTab.selectedCrewId ||
        isTabStreaming(activeTab.id)
      ) {
        return;
      }

      // Verificar tokens antes de enviar mensagem
      if (!checkTokensBeforeAction()) {
        return;
      }

      // Add user message to the tab
      const userMessage: Message = {
        id: Date.now().toString(),
        content: messageText,
        role: 'user',
        timestamp: new Date(),
        ...(attachments.length > 0
          ? {
              attachments: attachments.map(item => ({
                id: item.id,
                file_name: item.file_name,
                mime_type: item.mime_type,
                size_bytes: item.size_bytes,
                preview_url: item.preview_url,
              })),
            }
          : {}),
      };

      // Add assistant message placeholder
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        useCanvas: useCanvas,
        isStreaming: true,
        assistantPhase: 'thinking',
      };

      // Update tab messages
      const newMessages = [
        ...activeTab.messages,
        userMessage,
        assistantMessage,
      ];
      updateTabMessages(activeTab.id, newMessages);

      // Start streaming
      setTabStreaming(activeTab.id, true);
      cancelledTabsRef.current.delete(activeTab.id);
      const abortController = new AbortController();
      abortControllersRef.current[activeTab.id] = abortController;
      stopContextRef.current[activeTab.id] = {
        tabId: activeTab.id,
        messageId: assistantMessage.id,
        updateTabMessages,
        messages: newMessages,
        userMessage: messageText,
        restoreDraftMessage,
        getCurrentMessages,
      };

      try {
        const hasExistingSessionId = Boolean(activeTab.sessionId);
        const currentSessionId = activeTab.sessionId || `session_${Date.now()}`;

        if (!hasExistingSessionId) {
          updateTabSessionId(activeTab.id, currentSessionId);
        }

        const sourceIds = activeTab.selectedKnowledgeSources.map(
          (source: any) => source.id
        );
        const connectorSourceIds = (
          activeTab.selectedConnectorSources || []
        ).map((source: any) => source.id);
        const selectedSearchContextType: SearchContextType =
          activeTab.searchContextType || DEFAULT_SEARCH_CONTEXT_TYPE;

        // Validations dependent on the UI search context type.
        if (selectedSearchContextType === 'specific' && sourceIds.length === 0) {
          const rollbackMessages = activeTab.messages;
          updateTabMessages(activeTab.id, rollbackMessages);
          toast.error(t('conversations.specificRequiresSource'));
          return;
        }
        if (
          selectedSearchContextType === 'connectors' &&
          connectorSourceIds.length === 0
        ) {
          const rollbackMessages = activeTab.messages;
          updateTabMessages(activeTab.id, rollbackMessages);
          toast.error(t('conversations.connectorsRequiresSource'));
          return;
        }

        // Map UI-only 'connectors' to backend 'global' (backend only knows
        // global/specific/web; connectors are passed via connector_source_ids).
        const backendSearchContextType: 'global' | 'specific' | 'web' =
          selectedSearchContextType === 'connectors'
            ? 'global'
            : selectedSearchContextType;

        const isSpecificDocsMode = selectedSearchContextType === 'specific';
        const isConnectorsMode = selectedSearchContextType === 'connectors';

        const baseUserMessage =
          messageText.trim() || 'Analise as imagens anexadas e responda em português.';
        const finalUserMessage = useCanvas
          ? appendCanvasRulesToUserMessage(baseUserMessage)
          : baseUserMessage;

        let uploadedAttachments: Array<{
          attachment_id: string;
          file_name: string;
          mime_type: string;
          size_bytes: number;
          width?: number;
          height?: number;
        }> = [];
        const filesToUpload = attachments
          .map(item => item.file)
          .filter((file): file is File => Boolean(file));
        if (filesToUpload.length > 0) {
          uploadedAttachments =
            await executingAgentService.uploadConversationImages(filesToUpload);
        }

        const executePayload = {
          crew_id: activeTab.selectedCrewId,
          user_message: finalUserMessage,
          use_canvas: useCanvas,
          search_context_type: backendSearchContextType,
          ...(isSpecificDocsMode && sourceIds.length > 0
            ? { source_ids: sourceIds }
            : {}),
          ...(isConnectorsMode && connectorSourceIds.length > 0
            ? { connector_source_ids: connectorSourceIds }
            : {}),
          session_id: currentSessionId,
          ...(activeTab.selectedLlmModel
            ? { llm_model: activeTab.selectedLlmModel }
            : {}),
          ...(uploadedAttachments.length > 0
            ? { attachments: uploadedAttachments }
            : {}),
        };
        const endpoint: '/v1/execute-ai' | '/v1/execute-ai/live' =
          activeTab.useLiveData ? '/v1/execute-ai/live' : '/v1/execute-ai';
        const isLiveEndpoint = endpoint === '/v1/execute-ai/live';

        const stream = await executingAgentService.executeAiStreamString(
          executePayload,
          abortController.signal,
          endpoint
        );

        const updateAssistantMessage = (
          content: string,
          markAsUpdated: boolean = false,
          analysisArtifacts?: AnalysisArtifacts | null,
          imageAnalysis?: ImageAnalysisStatus | null
        ) => {
          const updatedMessages = newMessages.map(msg =>
            msg.id === assistantMessage.id
              ? {
                  ...msg,
                  content,
                  useCanvas: useCanvas,
                  isStreaming: false,
                  currentStatus: undefined,
                  assistantPhase: 'done' as AssistantPhase,
                  ...(analysisArtifacts !== undefined
                    ? { analysisArtifacts }
                    : {}),
                  ...(imageAnalysis !== undefined ? { imageAnalysis } : {}),
                  ...(markAsUpdated ? { streamUpdatedAt: Date.now() } : {}),
                }
              : msg
          );
          updateTabMessages(activeTab.id, updatedMessages);
        };

        const updateAssistantPartialMessage = (
          content: string,
          markAsUpdated: boolean = false,
          analysisArtifacts?: AnalysisArtifacts | null,
          imageAnalysis?: ImageAnalysisStatus | null
        ) => {
          const updatedMessages = newMessages.map(msg =>
            msg.id === assistantMessage.id
              ? {
                  ...msg,
                  content,
                  useCanvas: useCanvas,
                  isStreaming: true,
                  assistantPhase: 'answering' as AssistantPhase,
                  ...(analysisArtifacts !== undefined
                    ? { analysisArtifacts }
                    : {}),
                  ...(imageAnalysis !== undefined ? { imageAnalysis } : {}),
                  ...(markAsUpdated ? { streamUpdatedAt: Date.now() } : {}),
                }
              : msg
          );
          updateTabMessages(activeTab.id, updatedMessages);
        };

        const updateAssistantStatus = (
          status: string,
          phase: AssistantPhase = 'thinking'
        ) => {
          const updatedMessages = newMessages.map(msg =>
            msg.id === assistantMessage.id
              ? {
                  ...msg,
                  currentStatus: status,
                  isStreaming: true,
                  useCanvas: useCanvas,
                  assistantPhase: phase,
                }
              : msg
          );
          updateTabMessages(activeTab.id, updatedMessages);
        };

        /**
         * Normaliza linhas de stream (JSONL ou SSE) removendo prefixos.
         */
        const normalizeStreamLine = (line: string): string | null => {
          if (!line) return null;

          if (line.startsWith('data:')) {
            return line.replace(/^data:\s*/, '');
          }

          if (line.startsWith('event:') || line.startsWith(':')) {
            return null;
          }

          return line;
        };

        /**
         * Extrai o valor de uma string JSON para uma chave específica.
         * Suporta JSON inválido com quebras de linha não escapadas no valor.
         */
        const extractJsonStringValue = (
          raw: string,
          key: string
        ): string | null => {
          const keyIndex = raw.indexOf(key);
          if (keyIndex === -1) return null;

          const colonIndex = raw.indexOf(':', keyIndex + key.length);
          if (colonIndex === -1) return null;

          const quoteStart = raw.indexOf('"', colonIndex + 1);
          if (quoteStart === -1) return null;

          let result = '';
          let isEscaping = false;

          for (let i = quoteStart + 1; i < raw.length; i += 1) {
            const char = raw[i];

            if (isEscaping) {
              switch (char) {
                case 'n':
                  result += '\n';
                  break;
                case 'r':
                  result += '\r';
                  break;
                case 't':
                  result += '\t';
                  break;
                case '"':
                  result += '"';
                  break;
                case '\\':
                  result += '\\';
                  break;
                case 'u': {
                  const hex = raw.substring(i + 1, i + 5);
                  if (/^[0-9a-fA-F]{4}$/.test(hex)) {
                    result += String.fromCharCode(parseInt(hex, 16));
                    i += 4;
                  } else {
                    result += `\\u`;
                  }
                  break;
                }
                default:
                  result += char;
                  break;
              }
              isEscaping = false;
              continue;
            }

            if (char === '\\') {
              isEscaping = true;
              continue;
            }

            if (char === '"') {
              return result;
            }

            result += char;
          }

          return null;
        };

        /**
         * Faz parse tolerante de payloads JSON vindos do stream.
         */
        const parseStreamPayload = (
          raw: string,
          sseEvent?: string
        ): any | null => {
          try {
            const parsed = JSON.parse(raw);
            if (parsed?.type) {
              if (parsed.type === 'assistant') {
                return {
                  type: 'assistant',
                  message:
                    typeof parsed.message === 'string'
                      ? parsed.message
                      : typeof parsed.content === 'string'
                        ? parsed.content
                        : undefined,
                  analysisArtifacts: normalizeAnalysisArtifacts(
                    parsed.analysis_artifacts
                  ),
                };
              }
              return parsed;
            }
            if (sseEvent === 'done' && typeof parsed?.message === 'string') {
              return {
                type: 'done',
                message: parsed.message,
                analysisArtifacts: normalizeAnalysisArtifacts(
                  parsed.analysis_artifacts || parsed
                ),
                imageAnalysis: normalizeImageAnalysis(parsed.image_analysis),
              };
            }
            if (sseEvent === 'message' && typeof parsed?.message === 'string') {
              return { type: 'chunk', message: parsed.message };
            }
            return null;
          } catch {
            const typeMatch = raw.match(/"type"\s*:\s*"([^"]+)"/);
            if (!typeMatch) {
              if (sseEvent === 'done') {
                const messageValue = extractJsonStringValue(raw, '"message"');
                return messageValue
                  ? { type: 'done', message: messageValue }
                  : null;
              }
              if (sseEvent === 'message') {
                const messageValue = extractJsonStringValue(raw, '"message"');
                return messageValue
                  ? { type: 'chunk', message: messageValue }
                  : null;
              }
              return null;
            }

            const type = typeMatch[1];
            if (type === 'status') {
              const messageValue = extractJsonStringValue(raw, '"message"');
              return messageValue ? { type, message: messageValue } : { type };
            }

            if (type === 'message') {
              const messageValue = extractJsonStringValue(raw, '"message"');
              return messageValue ? { type, message: messageValue } : { type };
            }
            if (type === 'assistant') {
              const messageValue =
                extractJsonStringValue(raw, '"message"') ??
                extractJsonStringValue(raw, '"content"');
              const artifactPayload = normalizeAnalysisArtifacts({
                csv_base64: extractJsonStringValue(raw, '"csv_base64"') || '',
                pdf_base64: extractJsonStringValue(raw, '"pdf_base64"') || '',
              });
              return messageValue
                ? {
                    type: 'assistant',
                    message: messageValue,
                    analysisArtifacts: artifactPayload,
                  }
                : { type };
            }

            return { type };
          }
        };

        let hasReceivedMessage = false; // Flag to track if we've received the final message
        let buffer = ''; // Buffer para acumular chunks parciais
        let isSseStream = false;
        let currentSseEvent: string | null = null;
        let responseAccumulator = '';
        let latestAssistantMessage = '';
        let latestAssistantArtifacts: AnalysisArtifacts | null | undefined =
          undefined;
        let latestImageAnalysis: ImageAnalysisStatus | null | undefined =
          undefined;

        // Inicializar mensagem como streaming
        const initialMessages = newMessages.map(msg =>
          msg.id === assistantMessage.id
            ? { ...msg, isStreaming: true, useCanvas: useCanvas }
            : msg
        );
        updateTabMessages(activeTab.id, initialMessages);

        const extractJsonObjects = (input: string) => {
          const objects: string[] = [];
          let depth = 0;
          let inString = false;
          let escape = false;
          let startIndex = -1;
          let lastConsumedIndex = 0;

          for (let i = 0; i < input.length; i += 1) {
            const char = input[i];

            if (escape) {
              escape = false;
              continue;
            }

            if (char === '\\' && inString) {
              escape = true;
              continue;
            }

            if (char === '"') {
              inString = !inString;
              continue;
            }

            if (!inString) {
              if (char === '{') {
                if (depth === 0) {
                  startIndex = i;
                }
                depth += 1;
              } else if (char === '}') {
                depth -= 1;
                if (depth === 0 && startIndex !== -1) {
                  objects.push(input.slice(startIndex, i + 1));
                  lastConsumedIndex = i + 1;
                  startIndex = -1;
                }
              }
            }
          }

          const remaining = input.slice(lastConsumedIndex);
          return { objects, remaining };
        };

        const setArtifactsGenerating = (generating: boolean) => {
          const updatedMessages = newMessages.map(msg =>
            msg.id === assistantMessage.id
              ? { ...msg, artifactsGenerating: generating }
              : msg
          );
          updateTabMessages(activeTab.id, updatedMessages);
        };

        const handleParsedMessage = (data: any, sseEvent?: string) => {
          if (data.type === 'artifacts_generating') {
            setArtifactsGenerating(true);
            return false;
          }

          if (data.type === 'artifacts_ready') {
            setArtifactsGenerating(false);
            return false;
          }

          // ── keepalive: ignore for UI ──────────────────────────────
          if (data.type === 'keepalive') {
            return false;
          }

          // ── status events ─────────────────────────────────────────
          if (data.type === 'status') {
            const stage: string = data.stage || '';

            if (stage === 'execution_plan' || stage === 'finalizing') {
              if (data.message) {
                updateAssistantStatus(data.message, 'thinking');
              }
              return false;
            }

            if (!isLiveEndpoint && stage === 'answer_streaming_start') {
              // Transition to answering: reset accumulator, clear status
              responseAccumulator = '';
              latestAssistantMessage = '';
              const clearedMessages = newMessages.map(msg =>
                msg.id === assistantMessage.id
                  ? {
                      ...msg,
                      content: '',
                      currentStatus: undefined,
                      assistantPhase: 'answering' as AssistantPhase,
                      isStreaming: true,
                      useCanvas: useCanvas,
                    }
                  : msg
              );
              updateTabMessages(activeTab.id, clearedMessages);
              return false;
            }

            if (isLiveEndpoint && latestAssistantMessage) {
              return true;
            }

            // All other status events: just update currentStatus (replace, never accumulate)
            if (data.message) {
              updateAssistantStatus(data.message, 'thinking');
            }
            return false;
          }

          // ── waiting_progress chunks: treat as status, NOT content ─
          if (
            (data.type === 'chunk' || data.type === 'delta') &&
            data.stage === 'waiting_progress'
          ) {
            const progressText = (data.message || '').replace(/\n+$/, '').trim();
            if (progressText) {
              updateAssistantStatus(progressText, 'thinking');
            }
            return false;
          }

          // ── assistant snapshot (live) ─────────────────────────────
          if (data.type === 'assistant') {
            if (typeof data.message === 'string' && data.message) {
              latestAssistantMessage = data.message;
              responseAccumulator = data.message;
              if (data.analysisArtifacts) {
                latestAssistantArtifacts = data.analysisArtifacts;
              }
              if (data.imageAnalysis) {
                latestImageAnalysis = data.imageAnalysis;
              }
              updateAssistantPartialMessage(
                data.message,
                true,
                latestAssistantArtifacts,
                latestImageAnalysis
              );
            }
            return false;
          }

          // ── real content chunks ───────────────────────────────────
          if (data.type === 'chunk' || data.type === 'delta') {
            responseAccumulator += data.message || '';
            if (isLiveEndpoint) {
              latestAssistantMessage = responseAccumulator;
              updateAssistantPartialMessage(
                responseAccumulator,
                true,
                latestAssistantArtifacts,
                latestImageAnalysis
              );
            } else {
              updateAssistantPartialMessage(responseAccumulator);
            }
            return false;
          }

          // ── done event ────────────────────────────────────────────
          if (data.type === 'done' || data.type === 'message') {
            hasReceivedMessage = true;
            setArtifactsGenerating(false);
            const doneMessage =
              typeof data.message === 'string' ? data.message : '';

            // Prefer already-accumulated content; use done.message only as fallback
            if (!responseAccumulator && doneMessage) {
              responseAccumulator = doneMessage;
            }

            if (data.analysisArtifacts) {
              latestAssistantArtifacts = data.analysisArtifacts;
            }
            if (data.imageAnalysis) {
              latestImageAnalysis = data.imageAnalysis;
            }
            latestAssistantMessage = responseAccumulator || doneMessage || '';
            updateAssistantMessage(
              responseAccumulator || doneMessage || '',
              isLiveEndpoint,
              latestAssistantArtifacts,
              latestImageAnalysis
            );
            if (!isLiveEndpoint) {
              return true;
            }
            return false;
          }

          return false;
        };

        for await (const chunk of stream) {
          buffer += chunk;

          if (!isSseStream && /(^|\n)\s*data:\s*/.test(buffer)) {
            isSseStream = true;
          }

          if (isSseStream) {
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              const trimmedLine = line.trim();
              if (!trimmedLine) continue;
              if (trimmedLine.startsWith('event:')) {
                currentSseEvent = trimmedLine.replace(/^event:\s*/, '');
                continue;
              }

              const normalizedLine = normalizeStreamLine(trimmedLine);
              if (!normalizedLine) continue;

              try {
                const data = parseStreamPayload(
                  normalizedLine,
                  currentSseEvent || undefined
                );
                if (!data) {
                  throw new Error('Payload inválido');
                }
                const shouldStop = handleParsedMessage(
                  data,
                  currentSseEvent || undefined
                );
                if (shouldStop) {
                  break;
                }
              } catch (error) {
                if (hasReceivedMessage) {
                  continue;
                }

                if (
                  normalizedLine.includes('"type":"status"') ||
                  normalizedLine.startsWith('STATUS:')
                ) {
                  continue;
                }

                if (currentSseEvent === 'done') {
                  continue;
                }

                if (currentSseEvent === 'message') {
                  responseAccumulator += normalizedLine;
                  updateAssistantPartialMessage(responseAccumulator);
                  continue;
                }

                if (!normalizedLine.includes('"type"')) {
                  updateAssistantMessage(normalizedLine);
                }
              }
            }
          } else {
            const { objects, remaining } = extractJsonObjects(buffer);
            buffer = remaining;

            for (const jsonString of objects) {
              try {
                const data = parseStreamPayload(
                  jsonString,
                  isSseStream ? currentSseEvent || undefined : undefined
                );
                if (!data) {
                  throw new Error('Payload inválido');
                }
                const shouldStop = handleParsedMessage(data);
                if (shouldStop) {
                  break;
                }
              } catch (error) {
                if (hasReceivedMessage) {
                  continue;
                }
              }
            }
          }

          // Em endpoint padrão, parar ao receber mensagem final.
          // Em live, continuar para garantir que a última assistant seja usada.
          if (hasReceivedMessage && !isLiveEndpoint) {
            break;
          }
        }

        if (isLiveEndpoint && latestAssistantMessage) {
          hasReceivedMessage = true;
          updateAssistantMessage(
            latestAssistantMessage,
            true,
            latestAssistantArtifacts
          );
        }

        // Processar qualquer conteúdo restante no buffer após o loop
        if (!hasReceivedMessage && buffer.trim()) {
          const trimmedBuffer = buffer.trim();
          try {
            const normalizedBuffer = normalizeStreamLine(trimmedBuffer);
            if (normalizedBuffer) {
              const data = parseStreamPayload(
                normalizedBuffer,
                currentSseEvent || undefined
              );
              if (data) {
                const shouldStop = handleParsedMessage(
                  data,
                  currentSseEvent || undefined
                );
                if (shouldStop) {
                  return;
                }
              }
            }
          } catch (error) {
            if (hasReceivedMessage) {
              return;
            }
          }

          const { objects } = extractJsonObjects(trimmedBuffer);
          for (const jsonString of objects) {
            try {
              const data = parseStreamPayload(
                jsonString,
                isSseStream ? currentSseEvent || undefined : undefined
              );
              if (!data) {
                throw new Error('Payload inválido');
              }
              const shouldStop = handleParsedMessage(data);
              if (shouldStop) {
                break;
              }
            } catch (error) {
              // Ignore malformed chunk in trailing buffer.
            }
          }
        }

        // Garantir que isStreaming seja false ao finalizar
        if (!hasReceivedMessage) {
          const finalMessages = newMessages.map(msg =>
            msg.id === assistantMessage.id
              ? {
                  ...msg,
                  isStreaming: false,
                  currentStatus: undefined,
                  assistantPhase: 'done' as AssistantPhase,
                }
              : msg
          );
          updateTabMessages(activeTab.id, finalMessages);
        }

        // Atualizar tokens após interação com IA
        await refreshUser();
      } catch (error) {
        if (cancelledTabsRef.current.has(activeTab.id)) {
          return;
        }
        // Update the assistant message with error
        const errorMessages = newMessages.map(msg =>
          msg.id === assistantMessage.id
            ? {
                ...msg,
                content: `Erro: ${
                  error instanceof Error
                    ? error.message
                    : 'Erro ao enviar mensagem'
                }`,
              }
            : msg
        );
        updateTabMessages(activeTab.id, errorMessages);
        toast.error('Erro ao enviar mensagem');
      } finally {
        // Stop streaming
        setTabStreaming(activeTab.id, false);
        delete abortControllersRef.current[activeTab.id];
        delete stopContextRef.current[activeTab.id];
        cancelledTabsRef.current.delete(activeTab.id);
      }
    },
    [
      checkTokensBeforeAction,
      isTabStreaming,
      refreshUser,
      setTabStreaming,
      t,
    ]
  );

  const abortStreaming = useCallback(
    (tabId: string, restoreInputMessage: boolean) => {
      const tabAbortController = abortControllersRef.current[tabId];
      if (!tabAbortController) {
        return;
      }

      cancelledTabsRef.current.add(tabId);
      tabAbortController.abort();

      const stopContext = stopContextRef.current[tabId];
      if (stopContext) {
        const currentMessages =
          stopContext.getCurrentMessages?.(tabId) || stopContext.messages;
        const updatedMessages = currentMessages.map(msg =>
          msg.id === stopContext.messageId
            ? {
                ...msg,
                isStreaming: false,
                currentStatus: undefined,
                assistantPhase: 'done' as AssistantPhase,
              }
            : msg
        );
        stopContext.updateTabMessages(stopContext.tabId, updatedMessages);

        if (restoreInputMessage && stopContext.userMessage.trim()) {
          stopContext.restoreDraftMessage?.(tabId, stopContext.userMessage);
        }
      }

      setTabStreaming(tabId, false);
      delete abortControllersRef.current[tabId];
      delete stopContextRef.current[tabId];
    },
    [setTabStreaming]
  );

  const stopStreaming = useCallback(
    (tabId: string) => {
      abortStreaming(tabId, true);
    },
    [abortStreaming]
  );

  const handleSendMessage = useCallback(
    (
      activeTab: any,
      messageText: string,
      attachments: ConversationImageAttachment[] = [],
      updateTabMessages: (tabId: string, messages: Message[]) => void,
      updateTabSessionId: (tabId: string, sessionId: string) => void,
      clearDraftMessage?: (tabId: string) => void,
      restoreDraftMessage?: (tabId: string, message: string) => void,
      getCurrentMessages?: (tabId: string) => Message[]
    ) => {
      if (!messageText.trim() && attachments.length === 0) return;
      if (clearDraftMessage && activeTab?.id) {
        clearDraftMessage(activeTab.id);
      }

      sendMessage(
        messageText.trim(),
        attachments,
        activeTab,
        activeTab?.useCanvas ?? false,
        updateTabMessages,
        updateTabSessionId,
        restoreDraftMessage,
        getCurrentMessages
      );
    },
    [sendMessage]
  );

  return {
    handleSendMessage,
    isStreaming: Object.keys(streamingByTab).length > 0,
    isTabStreaming,
    stopStreaming,
  };
};
